import {
  Autocomplete,
  AutocompleteItem,
  Button,
  Divider,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  Select,
  SelectItem,
} from '@heroui/react';
import React, { useState } from 'react';
import { FiPlus, FiShare2, FiX } from 'react-icons/fi';

import {
  USER_SEARCH_MIN_CHARS,
  useMemberActions,
  useUserSearch,
} from '../../hooks';
import type { AssignableLocation } from '../../types';
import { locationDepth, locationLabel, tierStyle } from '../../utils';

export interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  /** Where roles may be granted, and which roles apply at each. */
  locations: AssignableLocation[];
  onChanged: () => Promise<void>;
}

const SEARCH_HINT = `Type at least ${USER_SEARCH_MIN_CHARS} characters to search`;

/** One row of the form: a place, and the role to hold there. */
interface AssignmentRow {
  locationId: string;
  roleId: string;
}

const EMPTY_ROW: AssignmentRow = { locationId: '', roleId: '' };

/** A place named under the tier above it, the way the directory shows it. */
const locationPath = (location: AssignableLocation): string => {
  const name = locationLabel(location.locationName, location.locationId);
  return location.parentName ? `${location.parentName} · ${name}` : name;
};

export const AddMemberModal: React.FC<AddMemberModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  locations,
  onChanged,
}) => {
  const { users, isSearching, searchUsers } = useUserSearch();
  const { addMember, assignRoles, isWorking, error, clearError } =
    useMemberActions(onChanged);

  const [userId, setUserId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [rows, setRows] = useState<AssignmentRow[]>([EMPTY_ROW]);

  const update = (index: number, next: AssignmentRow) =>
    setRows((current) => current.map((row, i) => (i === index ? next : row)));

  // One role per location is the service's rule, so a place already spoken for
  // is not offered again.
  const availableAt = (index: number) =>
    locations.filter(
      (location) =>
        !rows.some(
          (row, i) => i !== index && row.locationId === location.locationId
        )
    );

  const complete = rows.filter((row) => row.locationId && row.roleId);
  const canSubmit =
    userId !== '' && complete.length === rows.length && rows.length > 0;

  const close = () => {
    clearError();
    setUserId('');
    setSearchTerm('');
    setRows([EMPTY_ROW]);
    onClose();
  };

  const submit = async () => {
    const added = await addMember({ workspaceId, userId });
    if (!added) {
      return;
    }

    // A member with no roles is invisible to every read, so granting them is
    // part of adding someone rather than a second thing to remember. Every
    // pair goes in one call, so they all apply or none do.
    const assigned = await assignRoles({
      workspaceId,
      userId,
      assignments: complete,
    });
    if (!assigned) {
      return;
    }

    close();
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size="lg" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="text-xl">Add team member</ModalHeader>
        <Divider />
        <ModalBody className="gap-5 py-5">
          <div className="space-y-1.5">
            <p className="text-small font-medium text-foreground">
              Team member <span className="text-danger">*</span>
            </p>
            <p className="text-small text-default-500">
              Search for someone who already has a Choco account.
            </p>
            <Autocomplete
              aria-label="Team member"
              placeholder="Search by name, email or phone"
              description={
                searchTerm.length > 0 &&
                searchTerm.length < USER_SEARCH_MIN_CHARS
                  ? SEARCH_HINT
                  : undefined
              }
              variant="bordered"
              isLoading={isSearching}
              isDisabled={isWorking}
              items={users}
              selectedKey={userId || null}
              onInputChange={(value) => {
                setSearchTerm(value);
                searchUsers(value);
              }}
              onSelectionChange={(key) => setUserId(key ? String(key) : '')}
            >
              {(user) => (
                <AutocompleteItem
                  key={user.id}
                  textValue={user.name ?? user.id}
                >
                  <div className="flex flex-col">
                    <span className="text-sm text-foreground">
                      {user.name ?? 'Unnamed user'}
                    </span>
                    <span className="font-mono text-xs text-default-400">
                      {user.email ?? user.phone ?? user.id}
                    </span>
                  </div>
                </AutocompleteItem>
              )}
            </Autocomplete>
          </div>

          {rows.map((row, index) => {
            const picked = locations.find(
              (l) => l.locationId === row.locationId
            );

            return (
              <React.Fragment key={`assignment-${index}`}>
                <Divider />

                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FiShare2 className="text-default-500" />
                    <span className="font-medium text-foreground">
                      {index === 0
                        ? 'Main assignment'
                        : `Assignment ${index + 1}`}
                    </span>
                  </span>
                  {rows.length > 1 && (
                    <Button
                      size="sm"
                      variant="light"
                      color="danger"
                      isDisabled={isWorking}
                      onPress={() =>
                        setRows((current) =>
                          current.filter((_, i) => i !== index)
                        )
                      }
                    >
                      Remove
                    </Button>
                  )}
                </div>

                <div className="space-y-1.5">
                  <p className="text-small font-medium text-foreground">
                    Location <span className="text-danger">*</span>
                  </p>
                  <p className="text-small text-default-500">
                    Assign a location to this team member.
                  </p>
                  {picked ? (
                    <div className="flex items-center justify-between rounded-medium border border-default-200 px-3 py-2.5">
                      <span className="flex items-center gap-2">
                        <span
                          className={`h-2 w-2 shrink-0 rounded-full ${
                            tierStyle(picked.hierarchyType).dot
                          }`}
                          aria-hidden
                        />
                        <span className="text-small">
                          {locationPath(picked)}
                        </span>
                      </span>
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        aria-label="Clear location"
                        isDisabled={isWorking}
                        onPress={() => update(index, EMPTY_ROW)}
                      >
                        <FiX className="text-default-500" />
                      </Button>
                    </div>
                  ) : (
                    <Select
                      aria-label="Location"
                      placeholder="Pick a location"
                      variant="bordered"
                      isDisabled={isWorking || locations.length === 0}
                      selectedKeys={[]}
                      onSelectionChange={(keys) =>
                        // The old role may not apply at the new tier.
                        update(index, {
                          locationId: String([...keys][0] ?? ''),
                          roleId: '',
                        })
                      }
                    >
                      {availableAt(index).map((location) => {
                        const tier = tierStyle(location.hierarchyType);
                        const depth = locationDepth(location.hierarchyType);

                        return (
                          // The indent mirrors the Organization tab so the
                          // same tree is recognisable here.
                          <SelectItem
                            key={location.locationId}
                            textValue={locationPath(location)}
                          >
                            <div
                              className="flex items-center gap-2"
                              style={{ marginLeft: `${depth * 16}px` }}
                            >
                              <span
                                className={`h-2 w-2 shrink-0 rounded-full ${tier.dot}`}
                                aria-hidden
                              />
                              <span>
                                {locationLabel(
                                  location.locationName,
                                  location.locationId
                                )}
                              </span>
                              <span className="text-tiny text-default-500">
                                {tier.label}
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </Select>
                  )}
                </div>

                <div className="space-y-1.5">
                  <p className="text-small font-medium text-foreground">
                    Role <span className="text-danger">*</span>
                  </p>
                  <p className="text-small text-default-500">
                    Assign roles to manage what team members can do in your
                    organization.
                  </p>
                  {picked ? (
                    <RadioGroup
                      aria-label="Role"
                      value={row.roleId}
                      onValueChange={(roleId) =>
                        update(index, { ...row, roleId })
                      }
                      isDisabled={isWorking}
                      classNames={{ wrapper: 'gap-0' }}
                    >
                      {picked.roles.map((role) => (
                        <Radio
                          key={role.id}
                          value={role.id}
                          description={role.description}
                          classNames={{
                            base: 'max-w-full m-0 inline-flex flex-row-reverse items-center justify-between gap-4 rounded-none border border-default-200 px-4 py-3 first:rounded-t-medium last:rounded-b-medium [&:not(:first-child)]:border-t-0',
                            label: 'text-small text-foreground',
                            description: 'text-tiny text-default-500',
                          }}
                        >
                          {role.displayName}
                        </Radio>
                      ))}
                    </RadioGroup>
                  ) : (
                    <p className="rounded-medium border border-dashed border-default-200 px-4 py-3 text-small text-default-400">
                      Pick a location first — roles apply at one tier.
                    </p>
                  )}
                </div>
              </React.Fragment>
            );
          })}

          <Button
            size="sm"
            variant="flat"
            startContent={<FiPlus />}
            className="self-start"
            isDisabled={isWorking || rows.length >= locations.length}
            onPress={() => setRows((current) => [...current, EMPTY_ROW])}
          >
            Add another assignment
          </Button>

          {error && (
            <div className="rounded-medium bg-danger-50 p-3 text-small text-danger">
              {error}
            </div>
          )}
        </ModalBody>
        <Divider />
        <ModalFooter>
          <Button variant="bordered" onPress={close} isDisabled={isWorking}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={submit}
            isDisabled={!canSubmit || isWorking}
            isLoading={isWorking}
          >
            Add
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
